import { h, Component, Prop, Element, State, Watch } from '@stencil/core';

import { hasSlotContent } from '../../../../services/utils';

@Component({
  tag: 'pk-sequence-item',
  styleUrl: './pk-sequence-item.scss',
  shadow: true,
})
export class PkSequenceItem {
  // Own Properties
  // ====================

  private bodyElem: HTMLDivElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkSequenceItemElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() hasBody: boolean;

  // Public Property API
  // ========================

  /** If true, the vertical time line won't be displayed */
  @Prop({ reflect: true }) disableLine: boolean = false;

  /** If true, the component will be highlighted as the last element from the list */
  @Prop({ reflect: true }) last: boolean;

  /** Will set how the sequence item component will look and behave, as timeline or step */
  @Prop({ reflect: true }) type: 'timeline' | 'sequence' | 'stepper' = 'timeline';

  /** Will set the status of the sequence step item (available only if type = 'stepper') */
  @Prop({ reflect: true }) status: 'active' | 'done' | 'default' = 'default';

  // Prop lifecycle events
  // =======================

  @Watch('last')
  onLastPropChange() {
    this.handleLineDisplay();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.handleLineDisplay();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleLineDisplay = () => {
    if (!this.last || this.disableLine) return;

    if (!hasSlotContent(this.bodyElem)) {
      this.hasBody = false;
      return;
    }

    const bodySlotElem = this.el.shadowRoot.querySelector('slot[name="body"]');
    const bodySlotContent = (bodySlotElem as HTMLSlotElement).assignedElements({ flatten: true });

    this.hasBody = !!bodySlotContent.length && !!bodySlotContent[0].textContent;
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <div class="relative block" part="base">
        <span
          class={{
            'pk-sequence--item__line': true,
            [`${this.type}`]: true,
            [`${this.status}`]: this.type === 'stepper',
            hidden: this.disableLine || (this.last && !this.hasBody),
          }}
          aria-hidden="true"
          part="line"
        ></span>
        <div class="relative flex" part="item">
          <span
            class={{
              'pk-sequence--item__mark': true,
              [`${this.type}`]: true,
              [`${this.status}`]: this.type === 'stepper',
            }}
            part="mark"
          >
            {/* Icon | Text */}
            {this.type === 'stepper' && this.status === 'done' ? (
              <pk-icon name="actions-check" color="text-04" part="icon" />
            ) : (
              <slot name="mark" />
            )}
          </span>
          <div class="ml-s flex min-w-0 flex-1 flex-col justify-between" part="content">
            {/* Title */}
            <div
              class={{
                'pk-sequence--item__title m-0 flex items-center p-0': true,
                [`${this.type}`]: true,
                [`${this.status}`]: this.type === 'stepper',
              }}
              part="title"
            >
              <slot />
            </div>
            {/* Body */}
            <div
              class={{
                'pk-sequence--item__body m-0 mt-xs box-border min-h-[--sequence-item--min-height] p-0 pb-xl': true,
                empty: !this.hasBody,
              }}
              ref={(bodyElem: HTMLDivElement) => (this.bodyElem = bodyElem)}
              part="body"
            >
              <slot name="body" onSlotchange={this.handleLineDisplay} />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
